/**
 * ContactTagsContent — Account > People > Tags subtab (ADR-141 §4).
 *
 * Simple manager for the org's contact tags: list with usage counts,
 * create / rename via a dialog, delete behind a confirm dialog (deleting
 * a tag detaches it from every contact — worth a deliberate click).
 */
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Box, Center, Field, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  DialogComponent,
  InputField,
  RowActionsMenu,
  TableOptions,
  type Column,
} from '@themes/aegix';
import { useHasPermissions } from '../../../utils/hasPermission';
import {
  useContactTags,
  useCreateContactTag,
  useDeleteContactTag,
  usePatchContactTag,
} from '../../../hooks/queries/useContactTags';
import type { ContactTag } from '../../../services/contacts.service';
import { toaster } from '../../ui/toaster';

type TagDialogState =
  | { mode: 'create' }
  | { mode: 'edit'; tag: ContactTag }
  | null;

export function ContactTagsContent() {
  const { t } = useTranslation();
  const canAdd = useHasPermissions(['contacts.add_contacttag']);
  const canChange = useHasPermissions(['contacts.change_contacttag']);
  const canDelete = useHasPermissions(['contacts.delete_contacttag']);

  const { data: tags = [], isLoading, error } = useContactTags();
  const createTag = useCreateContactTag();
  const patchTag = usePatchContactTag();
  const deleteTag = useDeleteContactTag();

  const [searchValue, setSearchValue] = useState('');
  const [dialogState, setDialogState] = useState<TagDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactTag | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const columns: Column<ContactTag>[] = [
    {
      key: 'name',
      label: t('common.name', 'Name'),
      width: '220px',
      render: (row) => (
        <Text color="text.primary" data-e2e="tagName">
          {row.name}
        </Text>
      ),
    },
    {
      key: 'description',
      label: t('roles.description', 'Description'),
      render: (row) => (
        <Text color="text.secondary">{row.description || '—'}</Text>
      ),
    },
    {
      key: 'contact_count',
      label: t('contacts.title', 'Contacts'),
      width: '110px',
      render: (row) => (
        <Text color="text.secondary" data-e2e="tagContactCount">
          {row.contact_count}
        </Text>
      ),
    },
  ];

  const query = searchValue.trim().toLowerCase();
  const filteredTags = query
    ? tags.filter((tag) => tag.name.toLowerCase().includes(query))
    : tags;

  const openCreateDialog = () => {
    setName('');
    setDescription('');
    setShowErrors(false);
    setDialogState({ mode: 'create' });
  };

  const openEditDialog = (tag: ContactTag) => {
    setName(tag.name);
    setDescription(tag.description ?? '');
    setShowErrors(false);
    setDialogState({ mode: 'edit', tag });
  };

  const closeDialog = () => setDialogState(null);

  const nameError = name.trim() ? null : t('contacts.nameRequired', 'Name is required');

  const handleSaveTag = async () => {
    if (!dialogState) {
      return;
    }
    setShowErrors(true);
    if (nameError) {
      return;
    }
    const body = { name: name.trim(), description: description.trim() };
    try {
      if (dialogState.mode === 'create') {
        await createTag.mutateAsync(body);
        toaster.create({ title: t('contacts.tagCreated', 'Tag has been created'), type: 'success' });
      } else {
        await patchTag.mutateAsync({ tagId: dialogState.tag.id, body });
        toaster.create({ title: t('contacts.tagUpdated', 'Tag has been updated'), type: 'success' });
      }
      closeDialog();
    } catch {
      toaster.create({
        title:
          dialogState.mode === 'create'
            ? t('contacts.failedCreateTag', 'Failed to create tag')
            : t('contacts.failedUpdateTag', 'Failed to update tag'),
        type: 'error',
      });
    }
  };

  const handleDeleteTag = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteTag.mutateAsync(deleteTarget.id);
      toaster.create({ title: t('contacts.tagDeleted', 'Tag has been deleted'), type: 'success' });
      setDeleteTarget(null);
    } catch {
      toaster.create({ title: t('contacts.failedDeleteTag', 'Failed to delete tag'), type: 'error' });
    }
  };

  return (
    <>
      <TableOptions
        searchPlaceholder={t('contacts.searchTagsPlaceholder', 'Search tags...')}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        primaryAction={
          canAdd
            ? {
                label: t('contacts.addTag', 'Add Tag'),
                onClick: openCreateDialog,
                'data-e2e': 'addTag',
              }
            : undefined
        }
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : t('contacts.failedLoadTags', 'Failed to load tags')}
            </Text>
          </Center>
        ) : filteredTags.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary" data-e2e="tagsEmptyState">
              {t('contacts.noTags', 'No tags found')}
            </Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={filteredTags}
            keyExtractor={(row) => String(row.id)}
            onRowClick={canChange ? openEditDialog : undefined}
            getRowProps={() => ({ 'data-e2e': 'tagRow' })}
            renderRowActions={(row) => (
              <RowActionsMenu
                actions={[
                  {
                    label: t('common.rename', 'Rename'),
                    onClick: () => openEditDialog(row),
                    permission: canChange,
                    e2eTitle: 'editTag',
                  },
                  {
                    label: t('common.delete', 'Delete'),
                    variant: 'danger',
                    onClick: () => setDeleteTarget(row),
                    permission: canDelete,
                    e2eTitle: 'deleteTag',
                  },
                ]}
              />
            )}
            caption={t('contacts.tagsCaption', 'Contact tags')}
          />
        )}
      </Box>

      {/* Create / rename dialog */}
      <DialogComponent
        e2eTitle="ContactTagDialog"
        isOpen={dialogState !== null}
        onClose={closeDialog}
        headingText={dialogState?.mode === 'edit' ? t('contacts.renameTag', 'Rename Tag') : t('contacts.addTag', 'Add Tag')}
      >
        <VStack gap={4} align="stretch">
          <Field.Root invalid={showErrors && !!nameError}>
            <Field.Label htmlFor="contact-tag-name">{t('common.name', 'Name')}</Field.Label>
            <InputField
              id="contact-tag-name"
              placeholder={t('contacts.tagNamePlaceholder', 'Tag name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-e2e="tagNameInput"
            />
            {showErrors && nameError && (
              <Field.ErrorText data-e2e="tagNameError">
                {nameError}
              </Field.ErrorText>
            )}
          </Field.Root>
          <Field.Root>
            <Field.Label htmlFor="contact-tag-description">
              {t('roles.description', 'Description')}
            </Field.Label>
            <InputField
              id="contact-tag-description"
              placeholder={t('contacts.tagDescPlaceholder', 'Optional description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-e2e="tagDescriptionInput"
            />
          </Field.Root>
          <SimpleGrid columns={2} gap={4} width="100%">
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={closeDialog}
              data-e2e="cancel-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="button"
              onClick={() => void handleSaveTag()}
              disabled={createTag.isPending || patchTag.isPending}
              data-e2e="saveTag"
            >
              {t('common.save', 'Save')}
            </ButtonComponent>
          </SimpleGrid>
        </VStack>
      </DialogComponent>

      {/* Delete confirm dialog */}
      <DialogComponent
        e2eTitle="DeleteContactTagDialog"
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        headingText={t('contacts.deleteTagTitle', 'Delete Tag')}
      >
        <VStack gap={4} align="stretch">
          <Text color="text.primary" data-e2e="deleteTagMessage">
            {deleteTarget
              ? t(
                  'contacts.deleteTagConfirmMsg',
                  'Delete "{{name}}"? It is used by {{count}} contact{{plural}} and will be removed from all of them. Notification-tree rules targeting this tag stop matching.',
                  {
                    name: deleteTarget.name,
                    count: deleteTarget.contact_count,
                    plural: deleteTarget.contact_count === 1 ? '' : 's',
                  },
                )
              : ''}
          </Text>
          <SimpleGrid columns={2} gap={4} width="100%">
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={() => setDeleteTarget(null)}
              data-e2e="cancel-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="alert"
              type="button"
              onClick={() => void handleDeleteTag()}
              disabled={deleteTag.isPending}
              data-e2e="confirmDeleteTag"
            >
              {t('common.delete', 'Delete')}
            </ButtonComponent>
          </SimpleGrid>
        </VStack>
      </DialogComponent>
    </>
  );
}
